import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Active floorplan and elevation
    activeFloorplanId: null,
    activeElevation: null,
    activeScene: null,
    
    // Step navigation
    activeSteps: [],
    currentStepIndex: 0,
    
    // Product selections - keyed by surface name for stability
    stepSelections: {},
    // Example: { "Roof": { product, surfaceName, room, stepName, selectedAt, stepIndex } }
    
    // Sel strings per scene area (persisted selections per room)
    sceneSelStrings: {},
    // Example: { "Exterior": "123,456,789", "Kitchen": "111,222,333" }
    
    // Track which step indices have been completed (array for Redux serializability)
    completedStepIndices: [],
    
    // Chameleon visualization state
    specifier: '',
    selString: '-1',
    surfaceList: [],
    
    // UI state
    showAppliedProducts: false,
    vizLoading: false,
    
    // Selection history for undo/redo
    selectionHistory: {
        past: [],
        future: []
    }
};

const visualizerSlice = createSlice({
    name: 'visualizer',
    initialState,
    reducers: {
        // Set active floorplan ID
        setActiveFloorplanId: (state, action) => {
            state.activeFloorplanId = action.payload;
        },
        
        // Set active elevation and load its steps
        setActiveElevation: (state, action) => {
            const { elevation, steps } = action.payload;
            state.activeElevation = elevation;
            
            if (elevation && steps && steps.length > 0) {
                // Filter steps for this elevation
                const elevationId = elevation.Id || elevation.id;
                const elevationSteps = steps.filter(step => {
                    const stepElevationId = step.ElevationID || step.elevationID || step.ElevationId || step.elevationId;
                    return stepElevationId === elevationId;
                });
                
                // Parse params and organize steps
                state.activeSteps = elevationSteps.map(step => {
                    const params = {};
                    const paramsString = step.Params || step.params || '';
                    
                    // Parse params string: "title:Stone,series:8487,surface:Roof"
                    if (paramsString) {
                        paramsString.split(',').forEach(param => {
                            const [key, value] = param.split(':').map(s => s.trim());
                            if (key && value) {
                                params[key] = value;
                            }
                        });
                    }
                    
                    return {
                        name: step.Name || step.name,
                        elevationId: step.ElevationID || step.elevationID || step.ElevationId || step.elevationId,
                        room: step.Room || step.room,
                        params: params,
                        originalData: step
                    };
                });
                
                console.log('Active steps loaded:', state.activeSteps.length, 'steps for elevation', elevationId);
            } else {
                state.activeSteps = [];
            }
            
            // Reset to first step
            state.currentStepIndex = 0;
        },
        
        // Set active scene
        setActiveScene: (state, action) => {
            const newScene = action.payload;
            
            // Before switching, save the current scene's sel if we have one
            // This ensures we don't lose changes when rapidly switching
            if (state.activeScene && state.activeScene.area && state.activeScene.sel) {
                state.sceneSelStrings[state.activeScene.area] = state.activeScene.sel;
            }
            
            state.activeScene = newScene;
        },
        
        // Update a specific scene's sel string by area (safer than updateActiveSceneSel)
        updateSceneSelString: (state, action) => {
            const { area, sel } = action.payload;
            if (area && sel) {
                state.sceneSelStrings[area] = sel;
                // Also update activeScene.sel if it matches the area
                if (state.activeScene && state.activeScene.area === area) {
                    state.activeScene.sel = sel;
                }
            }
        },
        
        // Update active scene's sel string (after product applied)
        // DEPRECATED: Use updateSceneSelString instead for explicit area targeting
        updateActiveSceneSel: (state, action) => {
            if (state.activeScene) {
                state.activeScene.sel = action.payload;
                // Also save to sceneSelStrings keyed by area
                const area = state.activeScene.area;
                if (area) {
                    state.sceneSelStrings[area] = action.payload;
                }
            }
        },
        
        // Navigate to next step
        nextStep: (state) => {
            if (state.currentStepIndex < state.activeSteps.length - 1) {
                state.currentStepIndex += 1;
            }
        },
        
        // Navigate to previous step
        previousStep: (state) => {
            if (state.currentStepIndex > 0) {
                state.currentStepIndex -= 1;
            }
        },
        
        // Jump to specific step index
        jumpToStep: (state, action) => {
            const index = action.payload;
            if (index >= 0 && index < state.activeSteps.length) {
                state.currentStepIndex = index;
            }
        },
        
        // Select a product for a surface
        selectProduct: (state, action) => {
            const { product, surfaceName, stepIndex, selectionKey } = action.payload;
            const currentStep = state.activeSteps[stepIndex];
            
            if (!currentStep) return;
            
            // Save current state to history for undo
            state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
            state.selectionHistory.future = []; // Clear redo history
            
            // Use provided selectionKey or generate from stepName_room
            const key = selectionKey || `${currentStep.name}_${currentStep.room}`;
            
            // Store selection keyed by stepName_room
            state.stepSelections[key] = {
                product,
                surfaceName,
                room: currentStep.room,
                stepName: currentStep.name,
                stepParams: currentStep.params,
                selectedAt: new Date().toISOString(),
                stepIndex,
                selectionKey: key
            };
            
            // Mark this step index as completed (avoid duplicates)
            if (!state.completedStepIndices.includes(stepIndex)) {
                state.completedStepIndices.push(stepIndex);
            }
            
            console.log('Product selected for', key, ':', product.color || product.name);
            console.log('Step', stepIndex, 'marked as completed. Total completed:', state.completedStepIndices.length);
        },
        
        // Clear a selection
        clearSelection: (state, action) => {
            const surfaceName = action.payload;
            
            // Save to history
            state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
            state.selectionHistory.future = [];
            
            delete state.stepSelections[surfaceName];
        },
        
        // Clear all selections
        clearAllSelections: (state) => {
            // Save to history
            state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
            state.selectionHistory.future = [];
            
            state.stepSelections = {};
        },
        
        // Undo last selection
        undoSelection: (state) => {
            if (state.selectionHistory.past.length > 0) {
                // Move current to future
                state.selectionHistory.future.unshift(JSON.parse(JSON.stringify(state.stepSelections)));
                
                // Restore previous state
                const previousState = state.selectionHistory.past.pop();
                state.stepSelections = previousState;
            }
        },
        
        // Redo selection
        redoSelection: (state) => {
            if (state.selectionHistory.future.length > 0) {
                // Move current to past
                state.selectionHistory.past.push(JSON.parse(JSON.stringify(state.stepSelections)));
                
                // Restore future state
                const futureState = state.selectionHistory.future.shift();
                state.stepSelections = futureState;
            }
        },
        
        // Toggle applied products view
        toggleAppliedProducts: (state, action) => {
            state.showAppliedProducts = action.payload !== undefined 
                ? action.payload 
                : !state.showAppliedProducts;
        },
        
        // Set Chameleon specifier
        setSpecifier: (state, action) => {
            state.specifier = action.payload;
        },
        
        // Set Chameleon sel string
        setSelString: (state, action) => {
            state.selString = action.payload;
        },
        
        // Set surface list from Chameleon
        setSurfaceList: (state, action) => {
            state.surfaceList = action.payload;
        },
        
        // Set viz loading state
        setVizLoading: (state, action) => {
            state.vizLoading = action.payload;
        },
        
        // Reset visualizer state
        resetVisualizer: (state) => {
            return initialState;
        }
    }
});

export const {
    setActiveFloorplanId,
    setActiveElevation,
    setActiveScene,
    updateSceneSelString,
    updateActiveSceneSel,
    nextStep,
    previousStep,
    jumpToStep,
    selectProduct,
    clearSelection,
    clearAllSelections,
    undoSelection,
    redoSelection,
    toggleAppliedProducts,
    setSpecifier,
    setSelString,
    setSurfaceList,
    setVizLoading,
    resetVisualizer
} = visualizerSlice.actions;

// Selectors
export const selectCurrentStep = (state) => {
    const { activeSteps, currentStepIndex } = state.visualizer;
    return activeSteps[currentStepIndex] || null;
};

export const selectProgressPercentage = (state) => {
    const { completedStepIndices, activeSteps } = state.visualizer;
    const completedCount = completedStepIndices ? completedStepIndices.length : 0;
    const totalSteps = activeSteps.length;
    return totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
};

export const selectHasSelection = (surfaceName) => (state) => {
    return state.visualizer.stepSelections[surfaceName] !== undefined;
};

export const selectCanUndo = (state) => {
    return state.visualizer.selectionHistory.past.length > 0;
};

export const selectCanRedo = (state) => {
    return state.visualizer.selectionHistory.future.length > 0;
};

export default visualizerSlice.reducer;
